/**
 * Languages API Client with React Query Hooks
 *
 * Provides type-safe API client for Language Management using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPut } from 'services/api';
import { initialDataKeys } from 'services/initialDataApi';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

const LanguageSchema = z.object({
  code: z.string(),
  name: z.string(),
  nativeName: z.string(),
  supportedAsNative: z.boolean(),
  supportedAsTarget: z.boolean(),
  sortOrder: z.number().nullable().optional(),
  wordsEnabled: z.boolean(),
  waitlistEnabled: z.boolean(),
  flagEmoji: z.string().nullable().optional(),
  defaultLocaleId: z.string().nullable().optional(),
  displayLocalizationProvider: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});

// Export TypeScript types from Zod schemas
export type Language = z.infer<typeof LanguageSchema>;

// UpdateLanguageFlagsRequest type
export interface UpdateLanguageFlagsRequest {
  supportedAsNative: boolean;
}

// UpdateWordsEnabledRequest type
export interface UpdateWordsEnabledRequest {
  wordsEnabled: boolean;
}

// UpdateWaitlistEnabledRequest type
export interface UpdateWaitlistEnabledRequest {
  waitlistEnabled: boolean;
}

// UpdateDisplayLocalizationProviderRequest type
export interface UpdateDisplayLocalizationProviderRequest {
  displayLocalizationProvider: 'llm' | 'translate';
}

// SetDefaultLocaleRequest type
export interface SetDefaultLocaleRequest {
  localeCode: string | null;
}

// ============================================================================
// STEP 2: Define Transformation Functions
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase frontend types to snake_case API request
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

// ============================================================================
// STEP 3: Define Fetcher Functions
// ============================================================================

/**
 * Fetch all languages from admin API
 */
const fetchLanguages = async (): Promise<Language[]> => {
  try {
    const data = await apiGet<unknown[]>('/api/admin/languages');
    return z
      .array(LanguageSchema)
      .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
  } catch (error) {
    logger.error('Error fetching languages:', error);
    throw error;
  }
};

/**
 * Update language flags (supported_as_native)
 */
const updateLanguageFlags = async (
  languageCode: string,
  request: UpdateLanguageFlagsRequest
): Promise<Language> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(`/api/admin/languages/${languageCode}/flags`, payload);
    return LanguageSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating language flags:', error);
    throw error;
  }
};

/**
 * Update words_enabled flag for a language
 */
const updateWordsEnabled = async (
  languageCode: string,
  request: UpdateWordsEnabledRequest
): Promise<Language> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(
      `/api/admin/languages/${languageCode}/words-enabled`,
      payload
    );
    return LanguageSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating words enabled flag:', error);
    throw error;
  }
};

/**
 * Update waitlist_enabled flag for a language
 */
const updateWaitlistEnabled = async (
  languageCode: string,
  request: UpdateWaitlistEnabledRequest
): Promise<Language> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(
      `/api/admin/languages/${languageCode}/waitlist-enabled`,
      payload
    );
    return LanguageSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating waitlist enabled flag:', error);
    throw error;
  }
};

/**
 * Set default locale for a language
 */
const setDefaultLocale = async (
  languageCode: string,
  request: SetDefaultLocaleRequest
): Promise<Language> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(
      `/api/admin/languages/${languageCode}/default-locale`,
      payload
    );
    return LanguageSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error setting default locale:', error);
    throw error;
  }
};

/**
 * Update display localization provider for a language
 */
const updateDisplayLocalizationProvider = async (
  languageCode: string,
  request: UpdateDisplayLocalizationProviderRequest
): Promise<Language> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPut<unknown>(
      `/api/admin/languages/${languageCode}/display-localization-provider`,
      payload
    );
    return LanguageSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error updating display localization provider:', error);
    throw error;
  }
};

// ============================================================================
// STEP 4: Define React Query Hooks
// ============================================================================

/**
 * Fetch all languages
 */
export const useLanguages = () => {
  return useQuery({
    queryKey: ['admin-languages'],
    queryFn: fetchLanguages,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update language flags (supported_as_native)
 */
export const useUpdateLanguageFlags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      languageCode,
      request,
    }: {
      languageCode: string;
      request: UpdateLanguageFlagsRequest;
    }) => updateLanguageFlags(languageCode, request),
    onSuccess: (updatedLanguage) => {
      // Update languages list cache with the updated language
      queryClient.setQueryData<Language[]>(['admin-languages'], (oldData) => {
        if (!oldData) return [updatedLanguage];
        return oldData.map((lang) => (lang.code === updatedLanguage.code ? updatedLanguage : lang));
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-languages'] });

      // Invalidate initial data cache to refresh languages in initial data
      queryClient.invalidateQueries({ queryKey: initialDataKeys.admin() });
    },
  });
};

/**
 * Update words_enabled flag for a language
 */
export const useUpdateWordsEnabled = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      languageCode,
      request,
    }: {
      languageCode: string;
      request: UpdateWordsEnabledRequest;
    }) => updateWordsEnabled(languageCode, request),
    onSuccess: (updatedLanguage) => {
      // Update languages list cache with the updated language
      queryClient.setQueryData<Language[]>(['admin-languages'], (oldData) => {
        if (!oldData) return [updatedLanguage];
        return oldData.map((lang) => (lang.code === updatedLanguage.code ? updatedLanguage : lang));
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-languages'] });

      // Invalidate initial data cache to refresh languages in initial data
      queryClient.invalidateQueries({ queryKey: initialDataKeys.admin() });
    },
  });
};

/**
 * Update waitlist_enabled flag for a language
 */
export const useUpdateWaitlistEnabled = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      languageCode,
      request,
    }: {
      languageCode: string;
      request: UpdateWaitlistEnabledRequest;
    }) => updateWaitlistEnabled(languageCode, request),
    onSuccess: (updatedLanguage) => {
      // Update languages list cache with the updated language
      queryClient.setQueryData<Language[]>(['admin-languages'], (oldData) => {
        if (!oldData) return [updatedLanguage];
        return oldData.map((lang) => (lang.code === updatedLanguage.code ? updatedLanguage : lang));
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-languages'] });

      // Invalidate initial data cache to refresh languages in initial data
      queryClient.invalidateQueries({ queryKey: initialDataKeys.admin() });
    },
  });
};

/**
 * Set default locale for a language
 */
export const useSetDefaultLocale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      languageCode,
      request,
    }: {
      languageCode: string;
      request: SetDefaultLocaleRequest;
    }) => setDefaultLocale(languageCode, request),
    onSuccess: (updatedLanguage) => {
      // Update languages list cache with the updated language
      queryClient.setQueryData<Language[]>(['admin-languages'], (oldData) => {
        if (!oldData) return [updatedLanguage];
        return oldData.map((lang) => (lang.code === updatedLanguage.code ? updatedLanguage : lang));
      });

      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-languages'] });

      // Invalidate initial data cache to refresh languages in initial data
      queryClient.invalidateQueries({ queryKey: initialDataKeys.admin() });
    },
  });
};

/**
 * Update display localization provider for a language
 */
export const useUpdateDisplayLocalizationProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      languageCode,
      request,
    }: {
      languageCode: string;
      request: UpdateDisplayLocalizationProviderRequest;
    }) => updateDisplayLocalizationProvider(languageCode, request),
    onSuccess: (updatedLanguage) => {
      queryClient.setQueryData<Language[]>(['admin-languages'], (oldData) => {
        if (!oldData) return [updatedLanguage];
        return oldData.map((lang) => (lang.code === updatedLanguage.code ? updatedLanguage : lang));
      });

      queryClient.invalidateQueries({ queryKey: ['admin-languages'] });
      queryClient.invalidateQueries({ queryKey: initialDataKeys.admin() });
    },
  });
};
